import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { closeSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import ErrorPage from "../../Components/Others/error-page";

import renderAlertConfirmationModal from "../../Organisms/Modals/alert-confirmation-modal";

import {
  useGetPartnerCustomerInformation,
  useImagePreload,
  useIsCustomerFromPartnerOnboarded,
  useOnPartnerOnboardingLoad,
} from "../../CustomHooks/Queries";
import {
  useLoginWith2FACode,
  useRequestLogin2FACode,
  useSavePartnerCustomerInformation,
} from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import {
  dispatchAPIErrorToast,
  dispatchErrorToast,
  dispatchSuccessToast,
  LocalStorage,
  onSignOut,
  transformCountryCodeFormatToUSNumber,
} from "../../Utils/helpers";
import { APIQueryKeys, appUtilityKeys, debugProps } from "../../Utils/constants";

import { Terms } from "../../Assets/Images";
import {
  ArrowLeftCircle,
  CelebrationConfetti,
  ChevronRight,
  Mail,
  Phone,
  SecuredPersonalData,
} from "../../Assets/Icons";

const userNameTypes = { email: "email", phoneNumber: "phoneNumber" };

const PartnershipHeader = ({ partnerLogoUrl }) => {
  return (
    <header>
      <img className="logo" src={partnerLogoUrl} alt="" />
    </header>
  );
};

export default function PartnerSignUp() {
  const loginPreference = useRef(null);
  const resendOtpRef = useRef(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);

  const { show: showIntercom } = useIntercom();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { onModalClose } = modalContext;

  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const formProps = useForm();

  const partner_id = useMemo(() => new URLSearchParams(window.location.search).get("partnerId"), []);
  const partner_customer_id = useMemo(() => new URLSearchParams(window.location.search).get("customerReferenceId"), []);

  const hasRequiredParams = !!partner_id && !!partner_customer_id;

  const {
    isLoading: isLoadingPartnerOnboarding,
    data: partnerOnboarding,
    error: partnerOnboardingError,
    isError: isPartnerOnboardingError,
  } = useOnPartnerOnboardingLoad(partner_id, {
    enabled: !!partner_id,
  });

  const {
    isFetching: isCheckingIsCustomerFromPartnerOnboarded,
    data: customerFromPartnerOnboarded,
    error: customerFromPartnerOnboardedError,
    isError: isCustomerFromPartnerOnboardedError,
  } = useIsCustomerFromPartnerOnboarded({ partner_id, partner_customer_id }, { enabled: hasRequiredParams });

  const {
    isLoading: isGettingPartnerCustomerInformation,
    data: partnerCustomerInformation,
    error: partnerCustomerInformationError,
    isError: isPartnerCustomerInformationError,
  } = useGetPartnerCustomerInformation({ partner_id, partner_customer_id }, { enabled: hasRequiredParams });

  const { isLoading: isLoadingPartnershipLogo, data: partnerLogo } = useImagePreload(
    [partnerOnboarding?.data?.data?.partner_logo_url],
    {
      enabled: !!partnerOnboarding?.data?.data?.partner_logo_url,
    }
  );

  useEffect(() => {
    const isCustomerOnboarded = customerFromPartnerOnboarded?.data?.data?.is_customer_onboarded;
    setAgreedToTerms(isCustomerOnboarded);
  }, [customerFromPartnerOnboarded]);

  useEffect(() => {
    const agentId = new URLSearchParams(window.location.search).get("agentId");
    if (!!agentId) {
      LocalStorage.write(appUtilityKeys.agentId, agentId);
    }
  }, []);

  const {
    handleSubmit,
    getValues,
    formState: { isValid },
    resetField,
  } = formProps;

  const formattedUserName = useMemo(() => {
    return loginPreference.current === userNameTypes.email
      ? customerFromPartnerOnboarded?.data?.data?.email
      : transformCountryCodeFormatToUSNumber(customerFromPartnerOnboarded?.data?.data?.phone_number ?? "");
    //eslint-disable-next-line
  }, [loginPreference.current, customerFromPartnerOnboarded]);

  const { isLoading: isSavingPartnerInfo, mutate: SavePartnerInfo } = useSavePartnerCustomerInformation({
    onSuccess: () => {
      setAgreedToTerms(true);
      queryClient.refetchQueries(APIQueryKeys.isCustomerFromPartnerOnboarded);
      queryClient.refetchQueries(APIQueryKeys.getPartnerCustomerInformation);
    },
    onError: (err) => {
      if (err?.response?.status === 404) {
        renderAlertConfirmationModal(modalContext, {
          title: "Update Required",
          description: err?.response?.data?.error,
          CTAButtonText: "Got it",
          hideSecondaryCTAButton: true,
          onCTAButtonClick: () => onModalClose(),
        });
      } else {
        dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "savePartnerInfo" });
      }
    },
  });

  const { isLoading: isRequestingLogin2FACode, mutate: RequestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: () => {
      setShowOtpVerification(true);
      if (resendOtpRef.current) {
        resendOtpRef.current = false;
        dispatchSuccessToast(`New OTP code was sent at ${formattedUserName}`, { key: "request2FA" });
      } else {
        resetField("otp");
      }
    },
    onError: () => {
      dispatchErrorToast("Oops! Something went wrong!", { key: "request2FA" });
    },
    onSettled: () => (resendOtpRef.current = false),
  });

  const { isLoading: isLoggingInWith2FACode, mutate: LoginWith2FACode } = useLoginWith2FACode({
    onSuccess: (data) => {
      onSignOut({ redirectOnLogout: false });
      queryClient.resetQueries();
      onUserCredentialsUpdate(data);
      dispatchSuccessToast("Verification success");
      navigate("/login");
    },
    onError: (err) =>
      dispatchErrorToast(err?.response?.data?.error?.msg, {
        key: "loginWith2FA",
      }),
  });

  const onSubmit = () => {
    closeSnackbar();

    const userName =
      loginPreference.current === userNameTypes.email
        ? customerFromPartnerOnboarded?.data?.data?.email
        : customerFromPartnerOnboarded?.data?.data?.phone_number;

    let payload = { ...debugProps, [loginPreference.current]: userName };

    if (!showOtpVerification || resendOtpRef.current) {
      !isSignInLoading && RequestLogin2FACode(payload);
    } else {
      payload.phoneNumberCode = getValues("otp");
      !isSignInLoading && LoginWith2FACode(payload);
    }
  };

  const onFormError = (errors) => {
    dispatchErrorToast(errors.otp.message, { key: "otp" });
  };

  const handleGoToOTPVerification = (preference) => {
    loginPreference.current = preference;
    onSubmit();
  };

  const handleGoBackToLoginPreference = () => {
    loginPreference.current = null;
    setShowOtpVerification(false);
  };

  const isLoadingPageContent =
    isCheckingIsCustomerFromPartnerOnboarded ||
    isGettingPartnerCustomerInformation ||
    isLoadingPartnerOnboarding ||
    isLoadingPartnershipLogo;

  const isErrorPageContent =
    isCustomerFromPartnerOnboardedError ||
    isPartnerCustomerInformationError ||
    isPartnerOnboardingError ||
    !hasRequiredParams;

  const errorMessage =
    (isCustomerFromPartnerOnboardedError
      ? customerFromPartnerOnboardedError?.response?.data?.error
      : isPartnerCustomerInformationError
      ? partnerCustomerInformationError?.response?.data?.error
      : isPartnerOnboardingError
      ? partnerOnboardingError?.response?.data?.error
      : "Link Invalid") ?? "Network Error";

  const isSignInLoading = isRequestingLogin2FACode || isLoggingInWith2FACode;

  return isErrorPageContent ? (
    <ErrorPage
      isPageNotFound={customerFromPartnerOnboardedError?.response?.status === 404}
      errorMessage={errorMessage}
    />
  ) : (
    <div
      className={`onboarding-page partner-signup justify-content-${
        isLoadingPageContent ? "center" : "sm-center justify-content-start"
      }`}
    >
      {isLoadingPageContent ? (
        <CircularProgress sx={{ color: "var(--mist)" }} />
      ) : (
        <>
          {!agreedToTerms && (
            <div className="max-width-container justify-content-center">
              <PartnershipHeader partnerLogoUrl={partnerLogo?.[0]} />
              <div className="offer-and-terms">
                {!!partnerCustomerInformation?.data?.data?.rebate_message && (
                  <div className="offer-banner">
                    <CelebrationConfetti className="confetti" />{" "}
                    {partnerCustomerInformation?.data?.data?.rebate_message}
                  </div>
                )}
                <div className="partnership-info">{partnerOnboarding?.data?.data?.Partnership_description}</div>
                <div className="partnership-terms">
                  <img className="partnership-terms-image mb-4" src={Terms} alt="" />
                  <h2 className="partnership-title mb-2">Stake is the Cash Back network</h2>
                  <p className="partnership-description mb-3">
                    To access your Cash Back from Stake and partners, we need you to agree to our terms of service.
                  </p>
                  <a
                    className="terms-link mb-6"
                    href="https://www.stakerent.com/legal/terms-of-use"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Read our Terms of Service
                  </a>
                  <PrimaryButton
                    onClick={() =>
                      SavePartnerInfo({
                        partner_id,
                        partner_customer_id,
                        agent_id: LocalStorage.read(appUtilityKeys.agentId),
                      })
                    }
                    disabled={isSavingPartnerInfo}
                  >
                    Agree & Sign In {isSavingPartnerInfo && <CircularProgress />}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )}
          {agreedToTerms && !showOtpVerification && (
            <div className="max-width-container justify-content-center">
              <PartnershipHeader partnerLogoUrl={partnerLogo?.[0]} />
              <div className="login-preference">
                <SecuredPersonalData className="secure-icon mb-sm-6 mb-5" />
                <div className="title mb-1">Confirm Your Identity</div>
                <p className="description mb-sm-6 mb-5">Choose how you’d should like to sign in to “Stake”.</p>
                <button
                  className="preference-option mb-sm-6 mb-5"
                  onClick={handleGoToOTPVerification.bind(this, userNameTypes.phoneNumber)}
                >
                  <span className="icon-container">
                    <Phone />
                  </span>
                  <div className="option-info">
                    <div className="option-name">Phone Number</div>
                    <div className="option-value">
                      Text to {customerFromPartnerOnboarded?.data?.data?.masked_phone_number}
                    </div>
                  </div>
                  {loginPreference.current === userNameTypes.phoneNumber && isSignInLoading ? (
                    <CircularProgress className="loading" />
                  ) : (
                    <ChevronRight className="chevron" />
                  )}
                </button>
                <button
                  className="preference-option"
                  onClick={handleGoToOTPVerification.bind(this, userNameTypes.email)}
                >
                  <span className="icon-container">
                    <Mail />
                  </span>
                  <div className="option-info">
                    <div className="option-name">Email</div>
                    <div className="option-value">{customerFromPartnerOnboarded?.data?.data?.masked_email}</div>
                  </div>
                  {loginPreference.current === userNameTypes.email && isSignInLoading ? (
                    <CircularProgress className="loading" />
                  ) : (
                    <ChevronRight className="chevron" />
                  )}
                </button>
              </div>
              <button className="help-button position-static mt-5" onClick={() => showIntercom()}>
                Need help? Contact Support
              </button>
            </div>
          )}
          {agreedToTerms && showOtpVerification && (
            <>
              <FormProvider {...formProps}>
                <form className="form-card" onSubmit={handleSubmit(onSubmit, onFormError)} autoComplete="off">
                  <div className="card-header">
                    <button className="mb-2" onClick={handleGoBackToLoginPreference} type="button">
                      <ArrowLeftCircle />
                    </button>
                    <h1 className="text-4xl-semibold color--mist mt-1 mb-0">
                      {loginPreference.current === userNameTypes.email ? "Email" : "Phone Number"} Verification
                    </h1>
                    <p className="text-base-regular color--mist mt-2 mb-0">
                      Enter the 6-digit verification code sent to{" "}
                      <span className="username-badge">{formattedUserName}</span>
                    </p>
                  </div>
                  <div className="otp-field">
                    <OTPField />
                    <button
                      className="resend-otp-button mt-3"
                      onClick={() => {
                        resendOtpRef.current = true;
                        onSubmit();
                      }}
                      type="button"
                    >
                      Didn’t receive a text? Resend code
                    </button>
                  </div>
                  <PrimaryButton className="w-100" type="submit" disabled={!isValid || isSignInLoading}>
                    Next {isSignInLoading && <CircularProgress />}
                  </PrimaryButton>
                </form>
              </FormProvider>
              <button className="help-button" onClick={() => showIntercom()}>
                Trouble logging in? Contact Support
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
